import { useMemo } from "react"
import CoinGeckoWidget from "../components/fintech/CoinGeckoWidget"
import { BarChart3, Flame, ArrowLeftRight } from "lucide-react"
import { Link } from "react-router-dom"
import { useLang } from "../context/LangContext"


export default function CoinMarketTrackerPage() {


  const { lang: locale } = useLang()
  const copy = useMemo(() => {
    const en = {
      title: "Coin Market Tracker",
      subtitle: "Real-time crypto market information to make better decisions.",
      intro:
        "Welcome to Coin Market Tracker—built to give you a clear and updated view of the crypto market. Monitor, analyze, and improve your investment decisions with essential market tools.",
      sections: {
        tickerTitle: "Coin Market Ticker List",
        tickerDesc:
          "Check real-time prices, market cap, trading volume, and more for top cryptocurrencies. Stay aware of market movements and act with confidence.",
        heatmapTitle: "Coin Heatmap",
        heatmapDesc:
          "Visualize performance at a glance. Quickly see which coins are gaining or losing value to spot trends and potential opportunities.",
        converterTitle: "Coin Converter",
        converterDesc:
          "Convert between crypto and fiat with precision. Useful for estimating your portfolio value and planning your next move.",
      },
      note:
        "Widgets are provided by CoinGecko. Data and availability depend on their services.",
      controls: {
        language: "Language",
        en: "EN",
        es: "ES",
      },
      back: "Back",
    }

    const es = {
      title: "Coin Market Tracker",
      subtitle: "Información cripto en tiempo real para tomar mejores decisiones.",
      intro:
        "Bienvenido a Coin Market Tracker—diseñado para darte una visión clara y actualizada del mercado cripto. Monitorea, analiza y mejora tus decisiones con herramientas esenciales.",
      sections: {
        tickerTitle: "Lista de precios (Ticker)",
        tickerDesc:
          "Consulta precios en tiempo real, capitalización de mercado, volumen y más para las principales criptomonedas. Mantente atento a los movimientos del mercado y actúa con confianza.",
        heatmapTitle: "Mapa de calor (Heatmap)",
        heatmapDesc:
          "Visualiza el rendimiento de un vistazo. Identifica rápido qué monedas suben o bajan para detectar tendencias y oportunidades.",
        converterTitle: "Convertidor",
        converterDesc:
          "Convierte entre cripto y fiat con precisión. Ideal para estimar el valor de tu portafolio y planificar tu próximo movimiento.",
      },
      note:
        "Los widgets son provistos por CoinGecko. Los datos y disponibilidad dependen de sus servicios.",
      controls: {
        language: "Idioma",
        en: "EN",
        es: "ES",
      },
      back: "Volver",
    }

    return locale === "en" ? en : es
  }, [locale])

  // You can change the default coin here
  const defaultCoinId = "bitcoin"
  const defaultCurrency = "usd"

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-gray-100">
  

      <main className="mx-auto max-w-6xl px-6 py-10">
        {/* Top controls */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              <ArrowLeftRight className="h-4 w-4 rotate-180" />
              {copy.back}
            </Link>
          </div>
        </div>

        {/* Header */}
        <section className="mt-8">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">
            {copy.title}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {copy.subtitle}
          </p>
          <p className="mt-6 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-400">
            {copy.intro}
          </p>
        </section>

        {/* Widgets */}
        <div className="mt-10 grid grid-cols-1 gap-8">
          {/* Ticker */}
          <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-6">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-xl border border-gray-200 dark:border-gray-800 p-2">
                <BarChart3 className="h-5 w-5 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{copy.sections.tickerTitle}</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {copy.sections.tickerDesc}
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 overflow-hidden">
              <CoinGeckoWidget
                scriptSrc="https://widgets.coingecko.com/coingecko-coin-market-ticker-list-widget.js"
                html={`
                  <coingecko-coin-market-ticker-list-widget
                    coin-id="${defaultCoinId}"
                    currency="${defaultCurrency}"
                    locale="${locale}">
                  </coingecko-coin-market-ticker-list-widget>
                `}
              />
            </div>
          </section>

          {/* Heatmap */}
          <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-6">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-xl border border-gray-200 dark:border-gray-800 p-2">
                <Flame className="h-5 w-5 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{copy.sections.heatmapTitle}</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {copy.sections.heatmapDesc}
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 overflow-hidden">
              <CoinGeckoWidget
                scriptSrc="https://widgets.coingecko.com/coingecko-coin-heatmap-widget.js"
                html={`
                  <coingecko-coin-heatmap-widget
                    height="400"
                    locale="${locale}">
                  </coingecko-coin-heatmap-widget>
                `}
              />
            </div>
          </section>

          {/* Converter */}
          <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-6">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-xl border border-gray-200 dark:border-gray-800 p-2">
                <ArrowLeftRight className="h-5 w-5 text-gray-900 dark:text-gray-100" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">{copy.sections.converterTitle}</h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {copy.sections.converterDesc}
                </p>
              </div>
            </div>

            {/* NOTE: converter widget has color params. We'll keep it neutral. */}
            <div className="mt-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-black p-4 overflow-hidden">
              <CoinGeckoWidget
                scriptSrc="https://widgets.coingecko.com/coingecko-coin-converter-widget.js"
                html={`
                  <coingecko-coin-converter-widget
                    coin-id="${defaultCoinId}"
                    currency="${defaultCurrency}"
                    locale="${locale}"
                    background-color="#ffffff"
                    font-color="#4c4c4c">
                  </coingecko-coin-converter-widget>
                `}
              />
            </div>

            {/* Small note */}
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-500">
              {copy.note}
            </p>
          </section>
        </div>
      </main>
    </div>
  )
}
